import { useState, useEffect } from 'react';
import { X, Zap, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FloatingOfferBadge = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0 });

  useEffect(() => {
    // Check if dismissed
    const dismissed = localStorage.getItem('floating_offer_dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed).getTime();
      const now = new Date().getTime();
      if (now - dismissedTime < 6 * 60 * 60 * 1000) { // 6 hours
        setIsVisible(false);
        return;
      }
    }

    // Get end date from main offer
    const stored = localStorage.getItem('offer_end_date');
    if (!stored) return;

    const endDate = new Date(stored);

    const updateTime = () => {
      const now = new Date().getTime();
      const distance = endDate.getTime() - now;

      if (distance < 0) return;

      setTimeLeft({
        hours: Math.floor(distance / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      });
    };

    updateTime();
    const timer = setInterval(updateTime, 60000);
    return () => clearInterval(timer);
  }, []);

  const dismiss = () => {
    setIsVisible(false);
    localStorage.setItem('floating_offer_dismissed', new Date().toISOString());
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-24 right-6 z-50 transition-all duration-500 ${
        isMinimized ? 'w-14 h-14' : 'w-auto'
      }`}
    >
      {isMinimized ? (
        /* Minimized State - Just Icon */
        <button
          onClick={() => setIsMinimized(false)}
          className="w-14 h-14 rounded-full bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center shadow-lg shadow-[#F97316]/30 hover:scale-110 transition-transform animate-pulse"
        >
          <Zap size={24} className="text-white" fill="white" />
        </button>
      ) : (
        /* Expanded State */
        <div className="relative bg-gradient-to-r from-[#F97316] via-[#EC4899] to-[#8B5CF6] p-[2px] rounded-2xl shadow-2xl shadow-[#EC4899]/30 animate-slideIn">
          <div className="bg-[#0A0A0F] rounded-2xl p-4">
            {/* Close & Minimize Buttons */}
            <div className="absolute -top-2 -right-2 flex gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="w-6 h-6 rounded-full bg-gray-700 text-gray-400 hover:text-white flex items-center justify-center text-xs transition-colors"
              >
                −
              </button>
              <button
                onClick={dismiss}
                className="w-6 h-6 rounded-full bg-gray-700 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
              >
                <X size={12} />
              </button>
            </div>

            {/* Content */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center">
                <Zap size={20} className="text-white" fill="white" />
              </div>
              <div>
                <div className="text-white font-black text-xl">40% OFF</div>
                <div className="text-white/60 text-xs">Limited Time Only</div>
              </div>
            </div>

            {/* Timer */}
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-white/5 rounded-lg">
              <Clock size={14} className="text-[#F97316]" />
              <span className="text-white/80 text-sm">
                Ends in <span className="font-bold text-white">{timeLeft.hours}h {timeLeft.minutes}m</span>
              </span>
            </div>

            {/* CTA */}
            <button
              onClick={() => navigate('/book-consultation')}
              className="w-full py-2.5 text-sm font-bold text-white rounded-xl bg-gradient-to-r from-[#F97316] to-[#EC4899] hover:opacity-90 transition-opacity"
            >
              Claim Your Discount
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default FloatingOfferBadge;
